<script setup>
import { useTemplateRef, onBeforeUnmount, onMounted, computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);
const { filterTypes } = useConversationFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const { t } = useI18n();
const store = useStore();

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');
const filterContainerRef = ref(null);
const position = ref({ horizontal: 'center', vertical: 'bottom' });

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value);
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

const positionClasses = computed(() => {
  const classes = [];

  // Horizontal positioning
  if (position.value.horizontal === 'left') {
    classes.push('right-0');
  } else if (position.value.horizontal === 'center') {
    classes.push('left-1/2', '-translate-x-1/2');
  } else {
    // right (default)
    classes.push('left-0');
  }

  // Vertical positioning
  if (position.value.vertical === 'top') {
    classes.push('bottom-full', 'mb-2');
  } else {
    // bottom (default)
    classes.push('top-full', 'mt-2');
  }

  return classes;
});

const detectEdgePosition = async () => {
  await nextTick();

  if (!filterContainerRef.value) return;

  const container = filterContainerRef.value;
  const rect = container.getBoundingClientRect();
  const padding = 8; // Safety margin from edges

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Get parent element position to calculate relative positioning
  const parent = container.parentElement;
  const parentRect = parent?.getBoundingClientRect();

  if (!parentRect) return;

  // Calculate where container would be positioned for each option
  // and ensure 8px from screen edges

  // Horizontal edge detection
  const leftAlignedRight = parentRect.left + rect.width; // right edge when left-aligned (left-0)
  const rightAlignedLeft = parentRect.right - rect.width; // left edge when right-aligned (right-0)
  const centeredLeft = parentRect.left + parentRect.width / 2 - rect.width / 2;
  const centeredRight = centeredLeft + rect.width;

  // Check if container would overflow right edge when left-aligned
  if (leftAlignedRight > viewportWidth - padding) {
    // Check if right-align would work
    if (rightAlignedLeft >= padding) {
      // Right-align works
      position.value.horizontal = 'left';
    } else if (centeredLeft >= padding && centeredRight <= viewportWidth - padding) {
      // Center works
      position.value.horizontal = 'center';
    } else {
      // Nothing fits perfectly, use right-align as lesser evil
      position.value.horizontal = 'left';
    }
  }
  // Check if container would overflow left edge when right-aligned
  else if (parentRect.left < padding) {
    // Left-align works
    position.value.horizontal = 'right';
  }
  // Default: try to center if there's enough space
  else {
    if (centeredLeft >= padding && centeredRight <= viewportWidth - padding) {
      position.value.horizontal = 'center';
    } else if (parentRect.right < viewportWidth / 2) {
      // Parent is on left side of screen, align dropdown to left (left-0)
      position.value.horizontal = 'right';
    } else {
      // Parent is on right side of screen, align dropdown to right (right-0)
      position.value.horizontal = 'left';
    }
  }

  // Vertical edge detection
  // Check if container would overflow bottom edge
  if (parentRect.bottom + rect.height + padding > viewportHeight) {
    // Position above
    position.value.vertical = 'top';
  } else {
    // Position below (default)
    position.value.vertical = 'bottom';
  }
};

onMounted(() => {
  detectEdgePosition();
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleConversationFilterButton'] },
];
</script>

<template>
  <div
    ref="filterContainerRef"
    v-on-click-outside="outsideClickHandler"
    :class="[
      'absolute z-40 max-w-3xl overflow-visible w-auto border border-n-weak bg-white/50 dark:bg-slate-800/50 backdrop-blur-3xl shadow-lg rounded-xl p-6 grid gap-6 filter-container',
      positionClasses
    ]"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div v-if="props.isFolderView">
      <label class="border-b border-n-weak pb-6">
        <div class="text-n-slate-11 text-sm mb-2">
          {{ t('FILTER.FOLDER_LABEL') }}
        </div>
        <input
          v-model="folderNameLocal"
          class="py-1.5 px-3 text-n-slate-12 bg-n-alpha-1 text-sm rounded-lg reset-base w-full"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
      </label>
    </div>
    <TransitionGroup name="filter-fade" tag="ul" class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="`filter-${index}`">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </TransitionGroup>
    <div class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button
          v-if="isFolderView"
          sm
          solid
          blue
          :disabled="!folderNameLocal"
          @click="updateSavedCustomViews"
        >
          {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Simple fade animation for filter items */
.filter-fade-enter-active {
  transition: opacity 0.3s ease;
}

.filter-fade-leave-active {
  transition: opacity 0.3s ease;
  position: absolute;
}

.filter-fade-enter-from,
.filter-fade-leave-to {
  opacity: 0;
}

.filter-fade-move {
  transition: transform 0.3s ease;
}
</style>
